import { useMemo } from 'react';
import _get from 'lodash/get';
import { GeoJsonObject } from 'geojson';
import { FilterGroupingEnum } from 'graphql-common';
import removeTypenameDeep from '@lib/utils/removeTypenameDeep';
import { MapParams } from '@lib/components/Map/types';
import useSectorLocationCenterQueryHook from 'utils/fetch/hooks/useSectorLocationCenterQueryHook';
import useSiteLocationCenterQueryHook from 'utils/fetch/hooks/useSiteLocationCenterQueryHook';
import useSectorLocationsQueryHook from 'utils/fetch/hooks/useSectorLocationsQueryHook';
import useSiteLocationsQueryHook from 'utils/fetch/hooks/useSiteLocationsQueryHook';

const SITE_CENTER_DEFAULT_FILTERS = [
  {
    kind: {
      grouping: FilterGroupingEnum.And,
      predicate: { eq: 'point' },
    },
  },
];

const SECTOR_CENTER_DEFAULT_FILTERS = [
  {
    kind: {
      grouping: FilterGroupingEnum.And,
      predicate: { eq: 'boundary' },
    },
  },
];

export default function useSitesMapWidgetQueries({
  mapParams,
  showSectors,
  searchQuery,
}: {
  mapParams: MapParams | null;
  showSectors: boolean;
  searchQuery?: string;
}) {
  // Only fetch center if we don't have mapParams with center from localStorage
  const hasMapCenter = !!mapParams?.center;
  // Check if we have valid map bounds for location queries
  const hasValidMapBounds = !!(
    mapParams?.maxLat &&
    mapParams?.maxLon &&
    mapParams?.minLat &&
    mapParams?.minLon
  );

  // Site location center query - only needed when we don't have a saved center
  const siteLocationCenterQueryHookResult = useSiteLocationCenterQueryHook({
    defaultFilters: SITE_CENTER_DEFAULT_FILTERS,
    skip: hasMapCenter,
  });

  // Sector location center query - only needed when showing sectors and no saved center
  const sectorLocationCenterQueryHookResult = useSectorLocationCenterQueryHook({
    defaultFilters: SECTOR_CENTER_DEFAULT_FILTERS,
    skip: hasMapCenter || !showSectors,
  });

  const siteLocationCenterLoading =
    !hasMapCenter && siteLocationCenterQueryHookResult.firstLoading;

  // Site locations query - only run when we have valid map bounds
  const siteLocationsQueryHookResult = useSiteLocationsQueryHook({
    mapParams,
    searchQuery,
    skip: !hasValidMapBounds,
  });

  // Sector locations query - only when showing sectors AND have valid map bounds
  const sectorLocationsQueryHookResult = useSectorLocationsQueryHook({
    mapParams,
    searchQuery,
    skip: !showSectors || !hasValidMapBounds,
  });

  // Prepare map data with memoization to prevent unnecessary re-renders
  const siteLocationsData = siteLocationsQueryHookResult.data;
  const sectorLocationsData = sectorLocationsQueryHookResult.data;

  const mapData = useMemo(() => {
    const sitesMapData = removeTypenameDeep(siteLocationsData) as GeoJsonObject;
    const sectorsMapData = removeTypenameDeep(
      sectorLocationsData,
    ) as GeoJsonObject;

    if (showSectors) {
      const sectorFeatures = _get(sectorsMapData, 'features', []);
      const siteFeatures = _get(sitesMapData, 'features', []);
      return {
        ...(sectorsMapData || sitesMapData || {}),
        features: [...sectorFeatures, ...siteFeatures],
      } as unknown as GeoJsonObject;
    }
    return sitesMapData;
  }, [siteLocationsData, sectorLocationsData, showSectors]);

  const firstLoading =
    siteLocationsQueryHookResult.firstLoading ||
    (showSectors && sectorLocationsQueryHookResult.firstLoading) ||
    siteLocationCenterLoading;

  const isLoading =
    siteLocationsQueryHookResult.loading ||
    (showSectors && sectorLocationsQueryHookResult.loading);

  const siteLocationCenter = siteLocationCenterQueryHookResult.data;

  return {
    mapData,
    siteLocationCenterQueryHookResult,
    sectorLocationCenterQueryHookResult,
    siteLocationsQueryHookResult,
    sectorLocationsQueryHookResult,
    siteLocationCenterLoading,
    siteLocationCenter,
    firstLoading,
    isLoading,
  };
}
